import React from "react";
import { fallbackNumber } from "../utils/misc.util";
import { useRpiPerPage, UseRpiPerPageProps, UseRpiPerPageResult } from "./useRpiPerPage";

export interface UseRpiPaginationProps extends UseRpiPerPageProps {
  initialPage?: number;
  initialTotal?: number;
}

export interface UseRpiPaginationResult extends UseRpiPerPageResult {
  totalCount: number;
  setTotalCount: (total?: number | null) => void;
  page: number;
  pages: number;
  perPage: number;
  offset: number;
  setPage: (page: number) => void;
  nextPage: () => void;
  previousPage: () => void;
  firstPage: () => void;
  lastPage: () => void;
}

export function useRpiPagination({
  initialPage,
  initialTotal,

  ...perPageProps
}: UseRpiPaginationProps): UseRpiPaginationResult {
  const { perPage, setPerPage } = useRpiPerPage(perPageProps);

  const [totalCount, setTotalCountState] = React.useState<number>(() => fallbackNumber(initialTotal, 0));

  // Stable identity so callers can depend on it from an effect without re-running every render.
  const setTotalCount = React.useCallback((total?: number | null) => setTotalCountState(total || 0), []);
  const [storedPage, setPage] = React.useState<number>(() => fallbackNumber(initialPage, 0));

  const pages = React.useMemo(() => Math.ceil(totalCount / perPage), [totalCount, perPage]);

  // Derived instead of corrected in an effect. When `pages` shrinks past the stored page —
  // a filter narrowing the result set, say — reading it back as 0 keeps `page` in range on
  // the same render, rather than painting an out-of-range page and fixing it a commit later.
  const page = storedPage >= pages ? 0 : storedPage;

  // The clamp is also written back, during render, so it survives `pages` growing again. Deriving
  // alone would leave the out-of-range value stored and spring the user back to it the moment the
  // narrowed filter is cleared. Self-terminating: the next render reads `storedPage === page`.
  if (storedPage !== page) {
    setPage(page);
  }

  const offset = React.useMemo(() => page * perPage, [page, perPage]);

  const updatePage = (newPage: number) => {
    setPage(() => {
      if (newPage < 0) return 0;
      else if (newPage >= pages) return pages > 0 ? pages - 1 : 0;
      return newPage;
    });
  };

  React.useEffect(() => {
    return () => {
      // setTotalCount(0);
      setPage(0);
    };
  }, []);

  return {
    totalCount,
    setTotalCount,
    page,
    pages,
    offset,
    setPage: updatePage,
    nextPage: () => updatePage(page + 1),
    previousPage: () => updatePage(page - 1),
    firstPage: () => updatePage(0),
    lastPage: () => updatePage(pages),

    perPage,
    setPerPage,
  };
}
